import {
    type CrossAppAccount,
    type SignTypedDataParams,
    useCrossAppAccounts,
    usePrivy,
    type User
} from "@privy-io/react-auth";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    type Address,
    type Chain,
    createPublicClient,
    type EIP1193Provider,
    type EIP1193RequestFn,
    type EIP1474Methods,
    fromHex,
    hexToNumber,
    http,
    isHex,
    type RpcSchema
} from "viem";
import { GLYPH_PRIVY_APP_ID, STAGING_GLYPH_PRIVY_APP_ID } from "../constants";
import { createLogger } from "../utils";

interface PrivyUserWallets {
    signer?: Address | undefined;
    smartAccount?: Address | undefined;
}

interface RequestArguments {
    readonly method: string;
    readonly params?: readonly any[] | any;
}

const logger = createLogger("usePrivyCrossAppProvider");

type RpcMethodNames<rpcSchema extends RpcSchema> = rpcSchema[keyof rpcSchema] extends { Method: string }
    ? rpcSchema[keyof rpcSchema]["Method"]
    : never;
type EIP1474MethodNames = RpcMethodNames<EIP1474Methods>;

interface UsePrivyCrossAppEIP1193Props {
    chains: readonly Chain[];
    useStagingTenant?: boolean;
}

export const usePrivyCrossAppProvider = ({ chains, useStagingTenant }: UsePrivyCrossAppEIP1193Props) => {
    const { loginWithCrossAppAccount, linkCrossAppAccount, sendTransaction, signMessage, signTypedData } =
        useCrossAppAccounts();
    const { user, authenticated, ready: privyReady } = usePrivy();

    const [chainId, setChainId] = useState<string | null>(null);

    // Initialize chain ID with the first chain if available
    useEffect(() => {
        if (chains.length > 0 && !chainId) {
            setChainId(chains[0].id.toString());
        }
    }, [chains, chainId]);

    const passthroughMethods = {
        web3_clientVersion: true,
        web3_sha3: true,
        net_listening: true,
        net_peerCount: true,
        net_version: true,
        eth_blobBaseFee: true,
        eth_blockNumber: true,
        eth_call: true,
        eth_chainId: true,
        eth_coinbase: true,
        eth_estimateGas: true,
        eth_feeHistory: true,
        eth_gasPrice: true,
        eth_getBalance: true,
        eth_getBlockByHash: true,
        eth_getBlockByNumber: true,
        eth_getBlockTransactionCountByHash: true,
        eth_getBlockTransactionCountByNumber: true,
        eth_getCode: true,
        eth_getFilterChanges: true,
        eth_getFilterLogs: true,
        eth_getLogs: true,
        eth_getProof: true,
        eth_getStorageAt: true,
        eth_getTransactionByBlockHashAndIndex: true,
        eth_getTransactionByBlockNumberAndIndex: true,
        eth_getTransactionByHash: true,
        eth_getTransactionCount: true,
        eth_getTransactionReceipt: true,
        eth_getUncleByBlockHashAndIndex: true,
        eth_getUncleByBlockNumberAndIndex: true,
        eth_getUncleCountByBlockHash: true,
        eth_getUncleCountByBlockNumber: true,
        eth_maxPriorityFeePerGas: true,
        eth_newBlockFilter: true,
        eth_newFilter: true,
        eth_newPendingTransactionFilter: true,
        eth_protocolVersion: true,
        eth_sendRawTransaction: true,
        eth_uninstallFilter: true,
        zks_estimateFee: true
    };
    const passthrough = (method: EIP1474MethodNames) => !!passthroughMethods[method];

    const publicClients = useMemo(() => {
        return Object.fromEntries(
            chains.map((chain) => [
                chain.id,
                createPublicClient({
                    chain,
                    transport: http(chain.rpcUrls.default.http[0])
                })
            ])
        );
    }, [chains]);

    const getAddressesFromUser = (user: User | null): PrivyUserWallets => {
        if (!user) {
            return {
                smartAccount: undefined,
                signer: undefined
            };
        }
        logger.debug(
            "finding cross app account for app id",
            useStagingTenant ? STAGING_GLYPH_PRIVY_APP_ID : GLYPH_PRIVY_APP_ID
        );
        const crossAppAccount = user.linkedAccounts.find(
            (account) =>
                account.type === "cross_app" &&
                account.providerApp.id === (useStagingTenant ? STAGING_GLYPH_PRIVY_APP_ID : GLYPH_PRIVY_APP_ID)
        ) as CrossAppAccount | undefined;

        const smartAccount = crossAppAccount?.smartWallets?.[0]?.address;
        const signer = crossAppAccount?.embeddedWallets?.[0]?.address;

        return {
            smartAccount: smartAccount ? (smartAccount as Address) : undefined,
            signer: signer ? (signer as Address) : undefined
        };
    };

    const getAccounts = useCallback(
        async (promptLogin: boolean) => {
            if (!privyReady) {
                return [];
            }
            let contextUser = user;
            if (promptLogin) {
                if (!contextUser && !authenticated) {
                    contextUser = await loginWithCrossAppAccount({
                        appId: useStagingTenant ? STAGING_GLYPH_PRIVY_APP_ID : GLYPH_PRIVY_APP_ID
                    });
                } else if (!contextUser && authenticated) {
                    contextUser = await linkCrossAppAccount({
                        appId: useStagingTenant ? STAGING_GLYPH_PRIVY_APP_ID : GLYPH_PRIVY_APP_ID
                    });
                }
            }
            const { signer } = getAddressesFromUser(contextUser);
            if (signer) {
                // returning smartaccount makes wagmi's signMessage not to work
                //return [smartAccount, signer];
                return [signer];
            } else {
                return [];
            }
        },
        [user, authenticated, privyReady, loginWithCrossAppAccount, linkCrossAppAccount]
    );

    const eventListeners = useMemo(() => new Map<string, ((...args: any[]) => void)[]>(), []);

    const handleChainChanged = useCallback(
        (newChainId: string) => {
            if (newChainId !== chainId) {
                setChainId(newChainId);
                const listeners = eventListeners.get("chainChanged") || [];
                listeners.forEach((listener) => listener(newChainId));
            }
        },
        [chainId, eventListeners]
    );

    const handleRequest = useCallback(
        async (request: RequestArguments) => {
            const { method, params } = request;
            logger.debug("handleRequest", { method, params });

            // Get the current chainId from the request or use the stored chainId
            const currentChainId = (params?.chainId || params?.[0]?.chainId || +chainId!) as number;

            const publicClient = publicClients[currentChainId];

            if (passthrough(method as EIP1474MethodNames)) {
                return publicClient.request(request as any);
            }

            switch (method) {
                case "eth_requestAccounts": {
                    return await getAccounts(true);
                }
                case "eth_accounts": {
                    return await getAccounts(false);
                }
                case "wallet_addEthereumChain":
                case "wallet_switchEthereumChain": {
                    const request = params[0];
                    const chainIdHex = request.chainId;
                    if (!chainIdHex) {
                        throw new Error("Chain ID is required");
                    }
                    // chainId is hex most likely, convert to number
                    const targetChainId = isHex(chainIdHex) ? hexToNumber(chainIdHex) : chainIdHex;
                    const chain = Object.values(chains).find((c) => c.id === targetChainId);
                    if (!chain) {
                        throw new Error(`Chain ${targetChainId} not supported`);
                    }
                    handleChainChanged(targetChainId.toString());
                    return null;
                }
                case "wallet_revokePermissions":
                    // TODO: do we need to do anything here?
                    return null;
                case "eth_signTransaction":
                    throw new Error("eth_signTransaction is not supported");
                case "eth_sendTransaction": {
                    const transaction = params[0];
                    // Undo the automatic formatting applied by Wagmi's eth_signTransaction
                    // Formatter: https://github.com/wevm/viem/blob/main/src/zksync/formatters.ts#L114

                    // Required for gas sponsorship
                    // if (transaction.eip712Meta && transaction.eip712Meta.paymasterParams) {
                    //     transaction.paymaster = transaction.eip712Meta.paymasterParams.paymaster;
                    //     transaction.paymasterInput = toHex(transaction.eip712Meta.paymasterParams.paymasterInput);
                    // }

                    handleChainChanged(currentChainId.toString());
                    return await sendTransaction(
                        {
                            ...transaction,
                            chainId: currentChainId
                        },
                        {
                            address: transaction.from
                        }
                    );
                }
                case "eth_signTypedData_v4":
                    handleChainChanged(currentChainId.toString());
                    return await signTypedData(JSON.parse(params[1]) as SignTypedDataParams, {
                        address: params[0],
                        chainId: currentChainId
                    });
                case "eth_sign":
                    throw new Error("eth_sign is unsafe and not supported");
                case "personal_sign": {
                    handleChainChanged(currentChainId.toString());
                    return await signMessage(fromHex(params[0], "string"), {
                        address: params[1],
                        //address: "0x46E59553CC3be71d9Aa37E36C351DF12ED458ff5",
                        chainId: currentChainId
                    });
                }
                default:
                    throw new Error(`Unsupported request: ${method}`);
            }
        },
        [passthrough, publicClients, getAccounts, signMessage, chainId, handleChainChanged]
    );

    const provider: EIP1193Provider = useMemo(() => {
        return {
            on: (event, listener) => {
                eventListeners.set(event, [...(eventListeners.get(event) ?? []), listener]);
            },
            removeListener: (event, listener) => {
                eventListeners.set(
                    event,
                    (eventListeners.get(event) ?? []).filter((l) => l !== listener)
                );
            },
            request: handleRequest as EIP1193RequestFn<EIP1474Methods>
        };
    }, [handleRequest, eventListeners]);

    const ready = useMemo(() => {
        return (
            privyReady &&
            user &&
            authenticated &&
            user.linkedAccounts.some(
                (account) =>
                    account.type === "cross_app" &&
                    account.providerApp.id === (useStagingTenant ? STAGING_GLYPH_PRIVY_APP_ID : GLYPH_PRIVY_APP_ID)
            )
        );
    }, [privyReady, user, authenticated]);

    return {
        ready,
        provider,
        chainId: chainId ? parseInt(chainId) : chains[0].id
    };
};
